// DriverAssignmentSection.tsx
import React from 'react';
import user_icon from '../../../assets/icons/user.svg';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import { PiX } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';

interface DriverInterface {
  id: number;
  name: string;
  phone: string;
  profile_picture: string | null;
  client_delivery: any;
}

interface DriverAssignmentProps {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  drivers: DriverInterface[];
  selectedDrivers: number[];
  handleDriverSelect: (driver: DriverInterface) => void;
  handleDriverRemove: (driverId: number) => void;
}

const DriverAssignment: React.FC<DriverAssignmentProps> = ({
  searchQuery,
  setSearchQuery,
  drivers,
  selectedDrivers,
  handleDriverSelect,
  handleDriverRemove,
}) => {
  const { t } = useTranslation('client');

  return (
    <div className="flex my-4 pt-4 px-6">
      <div className="flex w-2/5 flex-col gap-4">
        <h6 className="text-subtitle-1 font-medium">
          {t('detail.driver_assignments')}
        </h6>
        <div className="relative">
          <input
            type="text"
            placeholder={t('manage_client.search_driver')}
            className="block w-full h-10 pl-4 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MagnifyingGlassIcon className="w-4 h-4 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none" />
        </div>
        <div className="flex flex-col overflow-y-auto h-56 pr-4">
          {drivers.length > 0 ? (
            drivers.map((driver) => (
              <div
                key={driver.id}
                className="flex flex-row items-center justify-between border-b px-2 py-3"
              >
                <div className="flex flex-row gap-4 items-center">
                  <img
                    src={driver.profile_picture || user_icon}
                    alt="User"
                    className="h-10 w-10 object-cover rounded-full"
                  />
                  <div>
                    <p className="text-body-2 font-medium text-neutral/300">
                      {driver.name}
                    </p>
                    <p className="text-caption text-neutral/75">
                      {driver.phone}
                    </p>
                  </div>
                </div>
                {driver.client_delivery === null ? (
                  selectedDrivers.includes(driver.id) ? (
                    <div className="w-8 h-8 flex items-center justify-center">
                      <CheckCircleIcon className="w-6 h-6 text-success" />
                    </div>
                  ) : (
                    <button
                      className="w-8 h-8 bg-primary/50 rounded-md flex items-center justify-center"
                      onClick={() => handleDriverSelect(driver)}
                      type="button"
                    >
                      <PlusIcon className="w-4 h-4 text-primary/300" />
                    </button>
                  )
                ) : (
                  <span className="text-caption text-neutral/100">
                    {t('manage_client.unavailable')}
                  </span>
                )}
              </div>
            ))
          ) : (
            <p>No drivers found</p>
          )}
        </div>
      </div>
      <div
        className={`flex flex-col w-3/5 ${
          selectedDrivers.length > 0
            ? 'items-start justify-start'
            : 'items-center justify-center'
        }`}
      >
        {selectedDrivers.length > 0 ? (
          <div className="flex flex-col items-start px-4">
            <h6 className="text-subtitle-1 font-medium pb-4">
              Total Assignment - {selectedDrivers.length}{' '}
              {selectedDrivers.length > 1 ? 'drivers' : 'driver'}
            </h6>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {drivers
                .filter((driver) => selectedDrivers.includes(driver.id))
                .map((driver) => (
                  <div
                    key={driver.id}
                    className="relative flex items-center gap-2 p-2 border rounded-md"
                  >
                    <img
                      src={driver.profile_picture || user_icon}
                      alt="User"
                      className="w-10 h-10 object-cover rounded-full"
                    />
                    <p className="w-full text-body-2 text-neutral/300 truncate">
                      {driver.name}
                    </p>
                    <button
                      onClick={() => handleDriverRemove(driver.id)}
                      className="text-primary/300"
                    >
                      <PiX size={18} />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 items-center justify-center">
            <img src={user_icon} alt="assign driver" className="h-14" />
            <h6 className="text-subtitle-2 font-medium">
              {t('manage_client.assign_driver_title')}
            </h6>
            <p className="text-caption text-center text-neutral/75">
              {t('manage_client.assign_driver_subtitle_1')}
              <br />
              {t('manage_client.assign_driver_subtitle_2')}{' '}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverAssignment;
